import { useMutation, useQuery } from '@apollo/client';
import moment from 'moment';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import ADD_MANUFACTURER_PICTURE from 'api/mutations/ADD_MANUFACTURER_PICTURE';
import ADD_TENANT_MANUFACTURER from 'api/mutations/ADD_TENANT_MANUFACTURER';
import SET_MANUFACTURER_NON_WORKING_DAYS from 'api/mutations/SET_MANUFACTURER_NON_WORKING_DAYS';
import GET_CURRENT_TENANT from 'api/queries/GET_CURRENT_TENANT';
import { dataValidationErrorHandler, registerDataValidationErrorMatcher, } from 'errors/dataValidationError';
import useKeyDownListener from 'hooks/useKeyDownListener';
import NoCalendarPng from 'images/v5/empty-states/calendar.png';
import ManufacturerSvg from 'images/v5/icons/menu/manufacturers.svg';
import PlusSvg from 'images/v5/icons/plus.svg';
import TrashSvg from 'images/v5/icons/trash-03.svg';
import Button from 'layout/atoms/Button/Button';
import CalendarRange from 'layout/atoms/CalendarRange/CalendarRange';
import Dropdown from 'layout/atoms/Dropdown/Dropdown';
import Input from 'layout/atoms/Input/Input';
import Label from 'layout/atoms/Label';
import Modal from 'layout/atoms/Modal/Modal';
import Photo from 'layout/atoms/Photo/Photo';
import TableGrid, { Body, Cell, Head, Row } from 'layout/atoms/TableGrid';
import EmptyState from 'layout/molecules/EmptyState/EmptyState';
import FormField from 'layout/molecules/FormField/FormField';
import FormSaveActions from 'layout/molecules/FormSaveActions/FormSaveActions';
import Grid from 'layout/molecules/Grid/Grid';
import { closeModal } from 'store/layoutSlice';
import { addManufacturer as addStoreManufacturer } from 'store/manufacturersSlice';
import { withAttachments } from 'utils/graphql';
import useManufacturerValidation from '../useManufacturerValidation';
function parseEmployeesCount(value) {
    if (value === null || value === undefined || value === '') {
        return 0;
    }
    const parsed = Number(value);
    return Number.isNaN(parsed) ? 0 : parsed;
}
function ManufacturerCreator() {
    var _a, _b;
    const { t } = useTranslation(['specification', 'common']);
    const dispatch = useDispatch();
    const [addManufacturer] = useMutation(ADD_TENANT_MANUFACTURER);
    const [setManufacturerNonWorkingDays] = useMutation(SET_MANUFACTURER_NON_WORKING_DAYS);
    const [addManufacturerPictureMutation] = useMutation(ADD_MANUFACTURER_PICTURE);
    const addManufacturerPicture = withAttachments(addManufacturerPictureMutation);
    const [manufacturerForm, setManufacturerForm] = useState({
        name: '',
        shortName: '',
        vatNumber: '',
        countryOfOrigin: '',
        addressPostalCode: '',
        addressCity: '',
        addressStreet: '',
        addressNumber: '',
        emailAddress: '',
        contactPhone: '',
        employeesCount: '',
    });
    const [calendarRange, setCalendarRange] = useState({
        startDate: null,
        endDate: null,
    });
    const [nonWorkingDays, setNonWorkingDays] = useState([]);
    const [pictureFile, setPictureFile] = useState(null);
    const [picturePreview, setPicturePreview] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error] = useState('');
    const { data: tenant } = useQuery(GET_CURRENT_TENANT);
    const tenantId = (_b = (_a = tenant === null || tenant === void 0 ? void 0 : tenant.me) === null || _a === void 0 ? void 0 : _a.currentTenant) === null || _b === void 0 ? void 0 : _b.id;
    const { errors, isValid, pendingFields, markTouched, validateAll } = useManufacturerValidation(manufacturerForm, t);
    const handleClose = () => {
        dispatch(closeModal({ name: 'manufacturerCreator' }));
    };
    const handleVariantChange = ({ target: { name, value }, }) => {
        setManufacturerForm((mfr) => (Object.assign(Object.assign({}, mfr), { [name]: value })));
    };
    const handlePictureAdd = useCallback((chosenFiles) => {
        const files = Array.isArray(chosenFiles) ? chosenFiles : [chosenFiles];
        if (files.length > 0) {
            const file = files[0];
            setPictureFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPicturePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    }, []);
    const handlePictureDelete = () => {
        setPictureFile(null);
        setPicturePreview(null);
    };
    const disabledDates = useMemo(() => {
        const dates = [];
        nonWorkingDays.forEach((range) => {
            const cursor = range.startDate.clone().startOf('day');
            const end = range.endDate.clone().startOf('day');
            while (cursor.isSameOrBefore(end, 'day')) {
                dates.push(cursor.clone());
                cursor.add(1, 'day');
            }
        });
        return dates;
    }, [nonWorkingDays]);
    const minDate = useMemo(() => moment().startOf('day'), []);
    const hasOverlap = (startDate, endDate) => {
        const cursor = startDate.clone().startOf('day');
        const end = endDate.clone().startOf('day');
        while (cursor.isSameOrBefore(end, 'day')) {
            if (disabledDates.some((day) => day.isSame(cursor, 'day'))) {
                return true;
            }
            cursor.add(1, 'day');
        }
        return false;
    };
    const handleCalendarChange = (startDate, endDate) => {
        setCalendarRange({ startDate, endDate });
    };
    const handleAddNonWorkingDay = () => {
        if (!calendarRange.startDate) {
            return;
        }
        const startDate = calendarRange.startDate.clone().startOf('day');
        const endDate = calendarRange.endDate
            ? calendarRange.endDate.clone().startOf('day')
            : startDate.clone();
        if (endDate.isBefore(startDate, 'day') || hasOverlap(startDate, endDate)) {
            return;
        }
        setNonWorkingDays((ranges) => [...ranges, { startDate, endDate }]);
        setCalendarRange({ startDate: null, endDate: null });
    };
    const handleRemoveNonWorkingDay = (index) => {
        setNonWorkingDays((ranges) => ranges.filter((_, idx) => idx !== index));
    };
    const formatRangeLabel = (range) => {
        const startLabel = range.startDate.format('DD.MM.YYYY');
        const endLabel = range.endDate.format('DD.MM.YYYY');
        return startLabel === endLabel ? startLabel : `${startLabel} - ${endLabel}`;
    };
    const handleFormSubmit = () => {
        if (!validateAll())
            return;
        setIsLoading(true);
        const nonWorkingDaysPayload = nonWorkingDays.map((range) => ({
            startDate: range.startDate.clone().startOf('day').toISOString(),
            endDate: range.endDate.clone().startOf('day').toISOString(),
        }));
        addManufacturer({
            variables: Object.assign(Object.assign({ tenantId }, manufacturerForm), { employeesCount: parseEmployeesCount(manufacturerForm.employeesCount) }),
            update(cache, newManufacturer) {
                var _a, _b, _c;
                const manufacturerId = (_c = (_b = (_a = newManufacturer.data) === null || _a === void 0 ? void 0 : _a.addManufacturer) === null || _b === void 0 ? void 0 : _b.manufacturer) === null || _c === void 0 ? void 0 : _c.id;
                const handleSaveSuccess = (manufacturer) => {
                    dispatch(addStoreManufacturer(Object.assign(Object.assign({}, newManufacturer), { data: Object.assign(Object.assign({}, newManufacturer.data), { addManufacturer: Object.assign(Object.assign({}, newManufacturer.data.addManufacturer), { manufacturer }) }) })));
                    dispatch(closeModal({ name: 'manufacturerCreator' }));
                };
                const applyNonWorkingDays = () => {
                    var _a, _b;
                    if (!manufacturerId || nonWorkingDaysPayload.length === 0) {
                        handleSaveSuccess((_b = (_a = newManufacturer.data) === null || _a === void 0 ? void 0 : _a.addManufacturer) === null || _b === void 0 ? void 0 : _b.manufacturer);
                        return Promise.resolve();
                    }
                    return setManufacturerNonWorkingDays({
                        variables: { manufacturerId, nonWorkingDays: nonWorkingDaysPayload },
                    })
                        .then((response) => {
                        handleSaveSuccess(response.data.setManufacturerNonWorkingDays);
                    })
                        .catch(dataValidationErrorHandler(dispatch));
                };
                if (manufacturerId && pictureFile) {
                    addManufacturerPicture({
                        variables: { manufacturerId, file: pictureFile },
                    })
                        .then((response) => {
                        if (nonWorkingDaysPayload.length === 0) {
                            handleSaveSuccess(response.data.addManufacturerPicture.manufacturer);
                            return Promise.resolve();
                        }
                        return setManufacturerNonWorkingDays({
                            variables: { manufacturerId, nonWorkingDays: nonWorkingDaysPayload },
                        })
                            .then((nonWorkingDaysResponse) => {
                            handleSaveSuccess(nonWorkingDaysResponse.data.setManufacturerNonWorkingDays);
                        })
                            .catch(dataValidationErrorHandler(dispatch));
                    })
                        .catch(dataValidationErrorHandler(dispatch));
                }
                else {
                    applyNonWorkingDays();
                }
            },
        }).catch((err) => {
            setIsLoading(false);
            dataValidationErrorHandler(dispatch)(err);
        });
    };
    registerDataValidationErrorMatcher();
    useKeyDownListener([
        { code: 'Enter', callback: handleFormSubmit },
        { code: 'Escape', callback: handleClose },
    ]);
    const pictureSrc = typeof picturePreview === 'string' ? picturePreview : null;
    return (React.createElement(Modal, { name: "manufacturerCreator", size: "m", header: React.createElement(Label, { weight: "strong" },
            React.createElement(ManufacturerSvg, null),
            " ",
            t('specification:add_manufacturer')), footer: React.createElement(FormSaveActions, { isValid: isValid, isLoading: isLoading, pendingFields: pendingFields, onSave: handleFormSubmit, onCancel: handleClose, validateAll: validateAll }) },
        React.createElement(Grid, { gap: 32 },
            React.createElement(Grid, null,
                React.createElement(Label, null, t('common:MANUFACTURER_AVATAR')),
                React.createElement(Photo, { theme: "avatar", size: "l", src: pictureSrc, alt: manufacturerForm.name || 'Manufacturer picture', onPhotoAdd: handlePictureAdd, onPhotoDelete: pictureFile ? handlePictureDelete : undefined })),
            React.createElement(Grid, null,
                React.createElement(Label, null, t('common:MANUFACTURER_BASIC_INFO')),
                React.createElement(FormField, { direction: "row", label: t('manufacturer_name_capitalize'), error: errors.name },
                    React.createElement(Input, { value: manufacturerForm.name, onChange: handleVariantChange, onBlur: () => markTouched('name'), name: "name", error: Boolean(errors.name) })),
                React.createElement(FormField, { direction: "row", label: t('manufacturer_shortname'), error: errors.shortName },
                    React.createElement(Input, { value: manufacturerForm.shortName, onChange: handleVariantChange, onBlur: () => markTouched('shortName'), name: "shortName", error: Boolean(errors.shortName) })),
                React.createElement(FormField, { direction: "row", label: t('specification:vat_number') },
                    React.createElement(Input, { value: manufacturerForm.vatNumber, onChange: handleVariantChange, name: "vatNumber" }))),
            React.createElement(Grid, null,
                React.createElement(Label, null, t('common:MANUFACTURER_ADDRESS')),
                React.createElement(FormField, { direction: "row", label: t('specification:country_of_origin') },
                    React.createElement(Input, { value: manufacturerForm.countryOfOrigin, onChange: handleVariantChange, name: "countryOfOrigin" })),
                React.createElement(FormField, { direction: "row", label: t('specification:address_postal_code') },
                    React.createElement(Input, { value: manufacturerForm.addressPostalCode, onChange: handleVariantChange, name: "addressPostalCode" })),
                React.createElement(FormField, { direction: "row", label: t('specification:address_city') },
                    React.createElement(Input, { value: manufacturerForm.addressCity, onChange: handleVariantChange, name: "addressCity" })),
                React.createElement(FormField, { direction: "row", label: t('specification:address_street') },
                    React.createElement(Input, { value: manufacturerForm.addressStreet, onChange: handleVariantChange, name: "addressStreet" })),
                React.createElement(FormField, { direction: "row", label: t('specification:address_number') },
                    React.createElement(Input, { value: manufacturerForm.addressNumber, onChange: handleVariantChange, name: "addressNumber" }))),
            React.createElement(Grid, null,
                React.createElement(Label, null, t('common:MANUFACTURER_CONTACT')),
                React.createElement(FormField, { direction: "row", label: t('specification:email_address') },
                    React.createElement(Input, { value: manufacturerForm.emailAddress, onChange: handleVariantChange, name: "emailAddress" })),
                React.createElement(FormField, { direction: "row", label: t('specification:contact_phone') },
                    React.createElement(Input, { value: manufacturerForm.contactPhone, onChange: handleVariantChange, name: "contactPhone" }))),
            React.createElement(Grid, null,
                React.createElement(Label, null, t('common:MANUFACTURER_DETAILS')),
                React.createElement(FormField, { direction: "row", label: t('specification:employees_count') },
                    React.createElement(Input, { value: manufacturerForm.employeesCount, onChange: handleVariantChange, name: "employeesCount", type: "number" })),
                React.createElement(TableGrid, { columns: [
                        ['200px', '1fr'],
                        ['72px', 'auto'],
                    ] },
                    React.createElement(Head, null,
                        React.createElement(Row, { head: true },
                            React.createElement(Cell, null, t('specification:non_working_days')),
                            React.createElement(Cell, { align: "right" },
                                React.createElement(Dropdown, { closeOnClick: false, trigger: React.createElement(Button, { size: "xs", theme: "ghost", shape: "rounded", leftIcon: React.createElement(PlusSvg, null) }) },
                                    React.createElement(CalendarRange, { startDate: calendarRange.startDate, endDate: calendarRange.endDate, onDateChange: handleCalendarChange, disabledDates: disabledDates, minDate: minDate }),
                                    React.createElement(Button, { color: "primary", onClick: handleAddNonWorkingDay, disabled: !calendarRange.startDate }, t('specification:add_non_working_days')))))),
                    nonWorkingDays.length > 0 ? (React.createElement(Body, null, nonWorkingDays.map((range, index) => (React.createElement(Row, { key: `${range.startDate.format('YYYY-MM-DD')}-${range.endDate.format('YYYY-MM-DD')}` },
                        React.createElement(Cell, null,
                            React.createElement("span", null, formatRangeLabel(range))),
                        React.createElement(Cell, { align: "right" },
                            React.createElement(Button, { size: "xs", theme: "ghost", color: "danger", shape: "rounded", leftIcon: React.createElement(TrashSvg, null), onClick: () => handleRemoveNonWorkingDay(index) }))))))) : (React.createElement(Body, null,
                        React.createElement(Row, null,
                            React.createElement(Cell, { span: 2, align: "right" },
                                React.createElement(EmptyState, { size: "s", icon: React.createElement("img", { src: NoCalendarPng, alt: "" }), header: t('specification:WORKING_DAYS_EMPTY_HEADER'), noSubheader: true }))))))),
            error && React.createElement("h5", { className: "errorText" }, error))));
}
export default memo(ManufacturerCreator);
